/**
 * DashboardLayout.tsx — the app shell.
 * Sidebar: the department SWITCHER in the header block, the Workspace rows
 *          (Home first, with the Sales counts), the remembered Tools caption,
 *          the account menu in the user footer (Settings · Theme · Log out).
 * Topbar:  derived breadcrumb (a list) + the per-page primary-action SLOT.
 *
 * SIDEBAR v2, SLICE S1 (2026-10-10, RULED by Klemen; the spec lives at the
 * operator's /root/klaxiora-sidebar-spec.md §2 and is never copied here) —
 * the expanded sidebar restructured and compacted, 224px:
 *   · The flat Departments group is GONE. The header block (K tile,
 *     "Klaxiora", the active department and a chevron) is a MENU BUTTON named
 *     "Switch department" opening a role="menu" popover ("Department menu"):
 *     the landing (overview.home, named "Dashboard" to match its breadcrumb)
 *     FIRST as a plain menuitem, then the nine departments as menuitemradio
 *     with aria-checked. Supersedes slice 2b's ruling (1), the flat switcher.
 *   · The pinned Dashboard row is GONE; "Home" is the active department's
 *     first page, first in Workspace — the Sales Dashboard row reads Home.
 *     Supersedes S14-1 option 3. The Workspace group carries no caption (the
 *     spec's main nav has no section label). Sales order (Q9): Home, Inbound
 *     Leads, Pipeline, Contacts, Accounts, Activities, Reports — Activities
 *     before Reports supersedes R6-6. The two counts are exactly as they were.
 *   · The theme capsule LEFT the bar (supersedes 2b (3)); the account menu
 *     gained a "Theme" group — Light / Dark / Auto — and Auto is new
 *     (theme/theme.ts resolves it from the device). The bar keeps the
 *     breadcrumb and the primary-action slot only.
 *   · No Search button / ⌘K (Q4), no Inbound Leads badge (Q5), no Calendar
 *     row (Q6): the Tools caption is a BUTTON (aria-expanded, open by
 *     default, remembered per tenant and user through lib/sidebarPref.ts)
 *     over Mission Control and, in Sales, Archive.
 *   · Rows are 34px / 13.5px with 17px icons at stroke 1.9, radius 8, a 1px
 *     gap between rows and 12px between blocks; the active row is white with
 *     a 0 1px 2px .08 lift, 600, its icon in the accent; hover is the new
 *     --k-nav-hover-bg (styles/klaxiora.css). The user block is 48px.
 *   · Both menus share ONE keyboard model, ShellMenu.tsx's useShellMenu()
 *     (lifted from slice 2b's account menu); a shell menu is a popover and
 *     never aria-modal — the single-key guard treats an open menu as inert
 *     through TYPING_SELECTOR's [role="menu"] (lib/keyboardGuard.ts).
 *   · NOT in S1: collapse, the icon rail, tooltips, auto-collapse and the
 *     drawer (S2/S3). Mission Control's own DEPARTMENTS list and NavSidebar
 *     are untouched (Q11). [S2 below adds the collapse and the rail.]
 *
 * SIDEBAR v2, SLICE S2 (2026-10-11, RULED by Klemen) — the collapsed 64px
 * icon rail:
 *   · A "Collapse sidebar" button (PanelLeft) at the right of the header row
 *     and an "Expand sidebar" button (PanelLeftOpen) at the bottom of the
 *     rail, above the avatar; each carries aria-expanded, aria-controls (the
 *     aside, #k-sidebar), aria-keyshortcuts and a title naming the platform's
 *     chord. The chord itself — ⌘\ or Ctrl+\ — is ONE document keydown
 *     listener here, for the shell's life (so never in Mission Control),
 *     behind lib/keyboardGuard.ts' sidebarChordEligible.
 *   · The state is device-level (lib/sidebarCollapsedPref.ts), read
 *     SYNCHRONOUSLY at first render — no /v1/me needed, so no snap on load —
 *     and written on each toggle. Default expanded.
 *   · The switch is data-sidebar on .dash-shell (index.css reads the rail's
 *     64 under "collapsed"); the grid column animates 200ms ease-out only
 *     after a toggle (data-sidebar-animate), never on first render, never
 *     under prefers-reduced-motion (the CSS).
 *   · The rail: the K tile (the switcher), the Workspace icons at 40×36, a
 *     hairline divider (none on the landing, which has no Workspace group),
 *     the Tools icons ALWAYS shown (the caption is not rendered; its stored
 *     state is neither read nor written while collapsed), the expand button
 *     and the 30px avatar. Labels are visually hidden (.k-rail-label), never
 *     removed, so every control keeps its accessible name; each rail icon
 *     carries a native title, the label and its count ("Pipeline 6"). The
 *     counts are hidden. Tooltips are S3.
 *   · Both menus open to the RIGHT of the rail at 196px (klaxiora.css); a
 *     toggle closes an open menu (useShellMenu's closeSignal) and returns
 *     focus to its trigger if focus was inside; focus on the toggle that
 *     disappears moves to its counterpart.
 *
 * SIDEBAR v2, SLICE S3a (2026-10-11, RULED by Klemen) — the rail's tooltips:
 *   · The rail icons (Workspace and Tools rows, the K tile, Expand, the
 *     avatar) lose their native titles; the same text rides data-tooltip and
 *     feeds ONE portalled tooltip (RailTooltip.tsx), placed 10px right and
 *     vertically centred, on hover and keyboard focus. The K tile reads
 *     "Klaxiora · {department}" (ruling 6).
 *   · The count moves from the title to aria-describedby: a visually hidden
 *     "Pipeline 6" beside the row (ruling 5). Expanded rows are unchanged.
 *
 * SIDEBAR v2, SLICE S3b (2026-10-11, RULED by Klemen) — the auto-collapse:
 *   · Below 1280 — matchMedia('(max-width: 1279.98px)'), so exactly 1280 stays
 *     expanded — the rail is FORCED (lib/sidebarForcedRail.ts), read
 *     synchronously at the first render: effective = forcedRail || stored.
 *   · The stored value is never written by a width change. While forced, a
 *     toggle (the button or the chord) expands in place for this session only
 *     and writes nothing; a second press returns to the rail; back above 1280
 *     the override is dropped and the stored state shows.
 *   · Width-driven changes do not animate; only a person's toggle does (and
 *     not under prefers-reduced-motion). Below 768 is the same as 768–1279
 *     until S3c (held). The toggle pair, its labels, aria-expanded and the
 *     tooltip follow the EFFECTIVE state.
 *
 * SIDEBAR v2, SLICE S3c (2026-10-11, RULED by Klemen) — the drawer below 768:
 *   · Below 768 — matchMedia('(max-width: 767.98px)'), read synchronously —
 *     the ONE aside becomes a drawer (data-sidebar="drawer"; a second copy
 *     would duplicate ids): closed at the first render, a fixed 224px panel
 *     over a scrim when open, showing the EXPANDED nav — never the rail, so no
 *     rail tooltips and no Collapse / Expand toggle; "Close navigation" sits in
 *     its header row instead. "Open navigation" (lucide Menu) sits at the left
 *     of the topbar, in drawer mode only, with aria-expanded / aria-controls.
 *   · Open, it is a modal: role dialog, aria-modal, named "Navigation drawer";
 *     the topbar and main are inert and Tab is trapped in the panel (no scroll
 *     lock — the shell is 100vh with overflow hidden). Esc, the scrim, the
 *     close button and ⌘\ / Ctrl+\ close it and return focus to the menu
 *     button; choosing a destination closes it without that return. The chord
 *     reaches it through keyboardGuard's NARROW exemption (the drawer's own
 *     modal only); the single-key L has none, so it stays inert while open.
 *   · Never stored. Crossing to 768 or wider closes it and drops the state;
 *     only a person's open slides in (none under reduced motion, none on the
 *     first render or a width change). Navigation only: page content below
 *     768 is the responsive pass's (register row 163).
 *
 * REDESIGN SLICE 2b (2026-09-27, the shell follow-up; ADOPTION.md §13.5 rules
 * it, §14 records it; register row 62) — the five decisions, all Klemen's;
 * (1) and (3) were SUPERSEDED by S1 above, kept here as history:
 *   1. The department switcher stays FLAT (nine rows, mockup-ruled). The
 *      boards show a dropdown; that is a DELIBERATE DEVIATION from the board,
 *      ruled 2026-09-27: the dropdown hides nine items behind a menu the
 *      inventory reads as lost, and the information architecture is not worth
 *      that yet. Recorded as his ruling, not a miss. [Superseded 2026-10-10:
 *      S1's switcher menu; the inventory's menu pass reads the items.]
 *   2. A TOOLS group in the sidebar: Mission Control (the mode switch), Calendar
 *      (still its coming-soon toast, still named "Calendar (coming soon)") and,
 *      in Sales, Archive (the same page, moved out of Workspace — it stays in
 *      DEPARTMENTS so the active department still derives from it). Mission
 *      Control and Calendar LEFT the bar: same names, same handlers. [S1
 *      removed the Calendar row, Q6.]
 *   3. The user footer is the ACCOUNT MENU: a menu button (aria-haspopup) whose
 *      menu holds Settings and Log out — the same handlers the bar's buttons
 *      had. The theme capsule stays visible in the bar (the frequent control
 *      stays one click). [Superseded 2026-10-10: the Theme group in the menu.]
 *   4. The per-page PRIMARY-ACTION SLOT: `primaryAction` renders in the bar to
 *      the right of the breadcrumb. Nothing passes it yet — each page slice
 *      moves its own button in when that page is re-shot. A page with no
 *      primary action renders NO slot: nothing is invented.
 *   5. The search capsule and the ⌘K listener LEFT dashboard mode (App.tsx
 *      keeps ⌘K for Mission Control, whose palette exists). Honest search over
 *      contacts, deals, accounts and leads is its own register row (63), OPEN;
 *      until it lands the boards show a search field the product does not
 *      have — a DELIBERATE DEVIATION, Klemen's, not a miss.
 *   Also here: the breadcrumb is a LIST (nav > ol > li), so assistive
 *   technology separates "Sales" from "Dashboard" — the chevron between them
 *   is decorative and aria-hidden, and as bare spans the two ran together
 *   (found 2026-09-27 on the served page by the §36.13 proof).
 *
 * REDESIGN SLICE 2 (2026-09-26, the calm-minimal shell; ADOPTION.md §13,
 * register row 49) — what changed and what deliberately did not:
 *   · The 34px page title in the bar became the boards' BREADCRUMB
 *     ("Sales › Pipeline": department in the secondary ink, page at 600),
 *     derived from DEPARTMENTS + activePage — no router, no link, no new
 *     affordance. The in-page 34px title is each page slice's job.
 *   · The brand block is the boards' 28px "K" square + "Klaxiora"; the
 *     department overline under it went (the switcher shows the department).
 *   · Group labels read sentence-case ("Departments", "Workspace"); rows are
 *     40px / 14px with 18px icons on the boards' sidebar ground. [S1: 34px /
 *     13.5px / 17px; the Workspace caption went with the spec.]
 *   · The Sales rows carry REAL counts — Pipeline the FENCED open-deal count
 *     from /v1/sales/reports (`openDealCount`, with the F-1d "(+k excluded)"
 *     suffix), Contacts the true total from /v1/sales/stats (`contacts.total`)
 *     — read on each Sales page change and re-read after a close or a create
 *     (the shared read, salesFigures.ts), rendered only once a read has
 *     succeeded (the slot rule: no number, no chip). This bullet said both
 *     counts came from /v1/sales/stats until 2026-10-02; the code never read
 *     the Pipeline count from there (register row 84).
 *   · The inert Dashboard tab of the shell switcher is GONE (ruled: no
 *     handler, no such control in the spec); Mission Control is the one
 *     button it leaves. The theme capsule, Calendar, Settings and Log out
 *     stayed in the bar at 36px / rounded-10 until slice 2b (above) moved all
 *     but the theme capsule — a live affordance was not dropped for a picture.
 *   · The five items that awaited a ruling here (the dropdown department
 *     switcher, the Tools group, the user kebab menu, a per-page
 *     primary-action slot, the search capsule + ⌘K) were RULED 2026-09-27 —
 *     the SLICE 2b block above is the record; this bullet is history. Main's
 *     padding is untouched: page padding is per page.
 *
 * S-TIER FOUNDATION (slice 1): restyled onto the .k-* component contract in
 * src/styles/klaxiora.css — 236px sidebar, mono group labels, .k-navrow rows,
 * .k-search capsule with its ⌘K chip, .k-segmented controls, .k-topbar and the
 * .k-page padding. This is a RESTYLE IN PLACE: the information architecture is
 * untouched. DEPARTMENTS, NAV_ICONS, PAGE_TITLES, navModel, NAV_HIDDEN and
 * SHOW_PLATFORM_SECTION are all exactly as they were — this slice changes how
 * the shell looks, never what it contains or where it goes.
 *
 * Two things are genuinely new, both required by the system:
 *   · The THEME CAPSULE (dark/light), backed by theme/theme.ts. [Gone from the
 *     bar since S1; the Theme group in the account menu is its successor.]
 *   · The count-chip SLOT on nav rows (.k-count). It is a slot and nothing
 *     more: no nav item has an honest per-item count wired yet, so nothing
 *     renders anywhere today. Each department slice wires its own real number.
 *     Rendering a fabricated count to match a reference screen would break the
 *     data-honesty invariant, which outranks the visual authority (CLAUDE.md).
 *
 * Colour/size values still flow from src/theme/tokens.ts, which now re-exports
 * the design system's custom properties — so this shell themes for free.
 */

import {
  Settings, Zap, LogOut, ChevronRight, ChevronDown, ChevronsUpDown, UserRound,
  LayoutGrid, Inbox as InboxIcon, Columns3, Users, ChartColumn, Activity,
  Archive, FileText, Send, CircleCheck, CircleX, Wallet, Target, GitBranch,
  Briefcase, UserPlus, Ticket, Banknote, GraduationCap, FileSearch, Bot, Building2,
  CalendarClock, Linkedin, Megaphone, TrendingUp, Mail, ScrollText, Scale,
  Gavel, History, BookOpen, ClipboardCheck, AlertTriangle, Package,
  CalendarDays, Moon, Sun, Monitor, Network, PanelLeft, PanelLeftOpen, Menu, X, type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { accent, edge, layout, surface, track } from '../../theme/tokens'
import { setTheme, useTheme, type ThemePreference } from '../../theme/theme'
import { cachedMeIdentity } from './api'
import { activeDepartment } from './navModel'
import { useShellMenu } from './ShellMenu'
import { useRailTooltip } from './RailTooltip'
import { loadSalesFigures, resetSalesFigures, useSalesFigures } from './salesFigures'
import { excludedSuffix } from './pages/sales/fenceCopy'
import { readToolsOpen, sidebarToolsPrefKey, writeToolsOpen } from '../../lib/sidebarPref'
import { readSidebarCollapsed, writeSidebarCollapsed } from '../../lib/sidebarCollapsedPref'
import { useForcedRail, useSidebarDrawerMode } from '../../lib/sidebarForcedRail'
import { sidebarChordEligible, isOtherModalOpen } from '../../lib/keyboardGuard'
import { useFocusTrap } from '../../lib/focusTrap'

// ── Department nav structure ──────────────────────────────────────────────────

// feature: when set, the item renders only if the tenant holds that enabled
// feature key (GET /v1/me features). Cosmetic — the server 403 is the boundary.
// The .k-count chip is wired per row from a read path (useSalesNavCounts);
// it renders ONLY when a real number arrived, never a placeholder or a zero
// standing in for "unknown" (data honesty outranks the reference screens).
interface NavItem  { id: string; label: string; feature?: string }
interface Department {
  id: string
  label: string
  items: NavItem[]
}

// ── Sidebar visibility flags (§14.D) — HIDDEN, never deleted. Flip a flag to
// restore. Hiding 'inbox' removes the sidebar path to the LIVE triage queue
// (inbox.triage); hiding the Platform section removes System Health / Run
// Analytics / Model Usage from the shell. Routes, PAGE_TITLES entries and
// page components all remain — the pages are unlinked, not gone.
const NAV_HIDDEN: Record<string, boolean> = { inbox: true }
const SHOW_PLATFORM_SECTION = false

const DEPARTMENTS: Department[] = [
  {
    // Tenant-level, department-agnostic: the triage queue reviews inbound
    // emails BEFORE they belong to any department — hence its own section.
    id: 'inbox', label: 'Inbox',
    items: [
      { id: 'inbox.triage', label: 'Triage' },
    ],
  },
  {
    id: 'sales', label: 'Sales',
    items: [
      // S1 (Q2/Q9): the department's first page is "Home" — the spec's word
      // for this row, and the pinned landing row it replaced was also named
      // Dashboard. The landing keeps "Dashboard" (its breadcrumb) in the
      // switcher menu. Other departments' first rows keep their own names.
      { id: 'sales.dashboard',  label: 'Home' },
      // L8-4 LOCKED (Klemen): Inbound Leads sits SECOND, immediately after
      // Home — top-of-funnel, per the full handoff tab order.
      { id: 'sales.leads',      label: 'Inbound Leads' },
      { id: 'sales.pipeline',   label: 'Pipeline' },
      { id: 'sales.contacts',   label: 'Contacts' },
      // Slice A (Phase 1 / 1.1): Accounts sits after Contacts — the entity
      // the contacts and deals link to. Every sales baseline moved one row.
      { id: 'sales.accounts',   label: 'Accounts' },
      // S1 (Q9): Activities before Reports — supersedes the mockup nav order
      // (R6-6), which had Reports directly after Contacts.
      { id: 'sales.activities', label: 'Activities' },
      { id: 'sales.reports',    label: 'Reports' },
      { id: 'sales.archive',    label: 'Archive' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    items: [
      { id: 'finance.overview',  label: 'Overview' },
      { id: 'finance.invoices',  label: 'Invoices' },
      { id: 'finance.issued-invoices', label: 'Issued Invoices' },
      { id: 'finance.approvals', label: 'Approvals' },
      { id: 'finance.rejections', label: 'Rejections' },
      { id: 'finance.expenses',  label: 'Expenses' },
      { id: 'finance.budgets',   label: 'Budgets' },
      { id: 'finance.reports',   label: 'Reports' },
    ],
  },
  {
    id: 'hr', label: 'HR',
    items: [
      { id: 'hr.overview',    label: 'Overview' },
      { id: 'hr.employees',   label: 'Employees' },
      { id: 'hr.lifecycle',   label: 'Lifecycle' },
      { id: 'hr.job-postings', label: 'Job Postings' },
      { id: 'hr.onboarding',  label: 'Onboarding' },
      // Org Chart sits before Tickets — the mockup's relative order (:3395).
      { id: 'hr.org-chart',   label: 'Org Chart' },
      { id: 'hr.tickets',     label: 'Tickets' },
    ],
  },
  {
    // Directly after HR, deliberately: payroll computes over HR's employee
    // records (profiles feed the ledger), so adjacency mirrors the data flow.
    // A standalone section, not an HR item — the pay-run view is a run across
    // employees, not a property of one.
    id: 'payroll', label: 'Payroll',
    items: [
      { id: 'payroll.pay-run', label: 'Pay Run' },
    ],
  },
  {
    id: 'recruitment', label: 'Recruitment',
    items: [
      { id: 'recruitment.overview',  label: 'Overview' },
      { id: 'recruitment.jobs',      label: 'Job Postings' },
      { id: 'recruitment.candidates',label: 'Candidates' },
      { id: 'recruitment.providers', label: 'Providers', feature: 'learning_providers' },
      { id: 'recruitment.cv',        label: 'CV Screening' },
    ],
  },
  {
    id: 'operations', label: 'Operations',
    items: [
      { id: 'operations.overview',  label: 'Overview' },
      { id: 'operations.agents',    label: 'Agents' },
      { id: 'operations.runs',      label: 'Active Runs' },
      { id: 'operations.scheduled', label: 'Schedules' },
      // M5a: Field Schedule — the mockup's Schedules tab slot (:3421,
      // between Active Runs and SOPs). Named apart from the cron
      // "Schedules" row above, which is agent-ops and untouched (the §B.4
      // name-collision rule: the field entity is tasks, not schedules).
      { id: 'operations.tasks',     label: 'Field Schedule' },
      // M1: SOPs — first real field-ops page; sits after Schedules per the
      // mockup's Operations nav order (klaxiora-crm-mockup-full.html :3422).
      { id: 'operations.sops',      label: 'SOPs' },
      // M3: Incidents — between SOPs and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.incidents', label: 'Incidents' },
      // M4: Assets — between Incidents and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.assets',    label: 'Assets' },
      // M2: Quality Checks — last mockup tab (:3425); LinkedIn stays last
      // as the live-only page.
      { id: 'operations.quality',   label: 'Quality Checks' },
      { id: 'operations.linkedin',  label: 'LinkedIn' },
    ],
  },
  {
    id: 'marketing', label: 'Marketing',
    items: [
      { id: 'marketing.overview',   label: 'Overview' },
      { id: 'marketing.campaigns',  label: 'Campaigns' },
      { id: 'marketing.analytics',  label: 'Analytics' },
      { id: 'marketing.email',      label: 'Email' },
    ],
  },
  {
    id: 'legal', label: 'Legal',
    items: [
      { id: 'legal.overview',   label: 'Overview' },
      { id: 'legal.contracts',  label: 'Contracts' },
      { id: 'legal.compliance', label: 'Compliance' },
    ],
  },
  {
    id: 'bids', label: 'Bids',
    items: [
      { id: 'bids.overview',   label: 'Overview' },
      { id: 'bids.active',     label: 'Active Bids' },
      { id: 'bids.history',    label: 'History' },
    ],
  },
]

// ── Workspace item icons (S14-2) — the mockup's nav is icon+label
// (klaxiora-crm-mockup-full.html :3351+, 17px .nav-item .icon). Keyed by
// route id; lucide equivalents of the mockup's ICON_* where the page exists
// there, in-spirit picks for live-only pages. The switcher's department rows
// stay icon-less (the mockup's cards are).
const NAV_ICONS: Record<string, LucideIcon> = {
  'overview.home':          LayoutGrid,
  'sales.dashboard':        LayoutGrid,
  'sales.leads':            InboxIcon,
  'sales.pipeline':         Columns3,
  'sales.contacts':         Users,
  'sales.accounts':         Building2,
  'sales.reports':          ChartColumn,
  'sales.activities':       Activity,
  'sales.archive':          Archive,
  'finance.overview':       LayoutGrid,
  'finance.invoices':       FileText,
  'finance.issued-invoices': Send,
  'finance.approvals':      CircleCheck,
  'finance.rejections':     CircleX,
  'finance.expenses':       Wallet,
  'finance.budgets':        Target,
  'finance.reports':        ChartColumn,
  'hr.overview':            LayoutGrid,
  'hr.employees':           Users,
  'hr.lifecycle':           GitBranch,
  'hr.job-postings':        Briefcase,
  'hr.onboarding':          UserPlus,
  'hr.org-chart':           Network,
  'hr.tickets':             Ticket,
  'payroll.pay-run':        Banknote,
  'recruitment.overview':   LayoutGrid,
  'recruitment.jobs':       Briefcase,
  'recruitment.candidates': Users,
  'recruitment.providers':  GraduationCap,
  'recruitment.cv':         FileSearch,
  'operations.overview':    LayoutGrid,
  'operations.agents':      Bot,
  'operations.runs':        Activity,
  'operations.scheduled':   CalendarClock,
  'operations.tasks':       CalendarDays,
  'operations.sops':        BookOpen,
  'operations.incidents':   AlertTriangle,
  'operations.assets':      Package,
  'operations.quality':     ClipboardCheck,
  'operations.linkedin':    Linkedin,
  'marketing.overview':     LayoutGrid,
  'marketing.campaigns':    Megaphone,
  'marketing.analytics':    TrendingUp,
  'marketing.email':        Mail,
  'legal.overview':         LayoutGrid,
  'legal.contracts':        ScrollText,
  'legal.compliance':       Scale,
  'bids.overview':          LayoutGrid,
  'bids.active':            Gavel,
  'bids.history':           History,
}

// Page title map
const PAGE_TITLES: Record<string, string> = {
  'overview.home':          'Dashboard',
  'inbox.triage':           'Triage',
  'overview.health':        'System Health',
  'automation.agents':      'All Agents',
  'automation.runs':        'Active Runs',
  'automation.scheduled':   'Scheduled Jobs',
  'scraping.linkedin':      'LinkedIn',
  'analytics.performance':  'Run Performance',
  'analytics.models':       'Model Usage',
  'analytics.feedback':     'Feedback Data',
  'sales.dashboard':        'Sales Dashboard',
  'sales.leads':            'Inbound Leads',
  'sales.pipeline':         'Pipeline',
  'sales.contacts':         'Contacts',
  'sales.accounts':         'Accounts',
  'sales.reports':          'Reports',
  'sales.activities':       'Activities',
  'sales.archive':          'Archive',
  'operations.agents':      'Agents',
  'operations.runs':        'Active Runs',
  'operations.scheduled':   'Schedules',
  'operations.tasks':       'Field Schedule',
  'operations.sops':        'SOPs',
  'operations.incidents':   'Incidents',
  'operations.assets':      'Assets',
  'operations.quality':     'Quality Checks',
  'operations.linkedin':    'LinkedIn',
  'settings.company':       'Settings',
  'finance.issued-invoices': 'Issued Invoices',
  'recruitment.providers':  'Learning Providers',
  'payroll.pay-run':        'Pay Run',
  'hr.org-chart':           'Org Chart',
}

// ── One sidebar row ───────────────────────────────────────────────────────────
// `current` drives .k-navrow[aria-current="page"] — the system keys its active
// state on a real accessibility attribute, not a class.
//
// `count` is the .k-count chip SLOT and is the data-honesty boundary: the chip
// renders only when a real number arrives, so today nothing renders anywhere.
// Each department slice wires its own count from its own read path.
//
// S1 geometry: a 17px icon at stroke 1.9 (the spec's nav icon); the row's
// height, radius and label size are .k-navrow's (klaxiora.css).
function NavRow({ id, label, Icon, current, count, trailing, onNavigate, onActivate, ariaLabel, title, kind = 'page', rail = false }: {
  id: string
  label: string
  Icon?: LucideIcon
  current: boolean
  /** A REAL number from a read path, with the rows a fence dropped beside it
   *  (F-1d). Absent → no chip. */
  count?: NavCount
  trailing?: React.ReactNode
  onNavigate: (page: string) => void
  /** Slice 2b: a TOOL row runs this instead of navigating (Mission Control
   *  switches mode). A page row leaves it undefined. */
  onActivate?: () => void
  /** Slice 2b: an explicit accessible name where the visible label is shorter
   *  than the control's meaning. No row needs one since S1 removed the
   *  Calendar row; the slot stays for the next tool that does. */
  ariaLabel?: string
  title?: string
  /** Slice 2b: `data-kind` — `page` rows navigate and are what the inventory
   *  walks; `tool` rows act in place. */
  kind?: 'page' | 'tool'
  /** S2: the row renders as a rail icon — its native title is the label and
   *  the count ("Pipeline 6"), the S1 titles stay on the expanded row. */
  rail?: boolean
}) {
  const railTitle = count ? `${label} ${navCountText(count)}` : label
  // S3a: in the rail the text rides data-tooltip (the portalled tooltip), and a
  // count reaches assistive tech as a description, not as a title.
  const descId = rail && count ? `k-rail-desc-${id.replace(/\./g, '-')}` : undefined
  return (
    <>
    <button
      type="button"
      onClick={() => (onActivate ? onActivate() : onNavigate(id))}
      aria-current={current ? 'page' : undefined}
      aria-label={ariaLabel}
      className="k-navrow w-full text-left cursor-pointer bg-transparent border-0"
      title={rail ? undefined : (title ?? count?.title)}
      data-tooltip={rail ? railTitle : undefined}
      aria-describedby={descId}
      data-kind={kind}
    >
      <span className="flex items-center gap-[12px] min-w-0">
        {Icon && <Icon size={17} strokeWidth={1.9} className="flex-shrink-0" />}
        <span className="k-truncate k-rail-label">{label}</span>
      </span>
      {/* The chip is aria-hidden so the row's accessible NAME stays its label
          ("Pipeline", not "Pipeline 9 (+1 excluded)"): a count changes daily
          and must not rename the control (the inventory keys on the name);
          the button's title carries the full sentence instead. */}
      {count ? <span className="k-count" aria-hidden="true">{navCountText(count)}</span> : trailing}
    </button>
    {/* The description sits OUTSIDE the button: inside, its text would join the
        accessible NAME ("Pipeline Pipeline 6"). sr-only is absolutely positioned,
        so it takes no part in the group's flex layout. */}
    {descId && <span id={descId} className="sr-only">{railTitle}</span>}
    </>
  )
}

// ── Nav counts (slice 2) ──────────────────────────────────────────────────────
// The boards show a count beside Pipeline. The honest numbers for the Sales
// rows: /v1/sales/reports' openDealCount — the FENCED open-deal count with
// openExcludedNonGbp beside it (§26 F-1), the SAME figure the Dashboard's
// "Open deals" card shows, so the chip and the card can never disagree
// (/stats' totalDeals is all stages, closed included — measured 16 vs 9 on
// Fourth Coffee, so it is NOT the chip) — and /v1/sales/stats' contacts.total,
// a true COUNT. Accounts and Activities (ruled 2026-10-12) ride the same /stats
// read: accounts.total (every account of the tenant) and activities.pending
// (status 'pending' — the Activities page's "pending" uses the same rule). A
// backend that predates them sends no key, and that row shows no chip — never
// NaN, never "undefined". Both come from the ONE shared read (salesFigures.ts, register
// row 84): the Pipeline board's header reads the same figures, and a close or
// a create re-reads them for both. A Sales page change re-reads; leaving Sales
// drops the figures. Both halves must have landed for either chip to render —
// a failed FIRST read renders nothing (the chip is a slot, never a
// placeholder); a failed RE-read leaves the last figures standing.
export interface NavCount { value: number; excluded?: number; title?: string }
export function navCountText(c: NavCount): string {
  const tail = excludedSuffix(c.excluded)
  return tail ? `${c.value} ${tail}` : String(c.value)
}
/** A count the wire actually sent: a finite, non-negative integer. */
function isCount(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0
}
function useSalesNavCounts(active: boolean, activePage: string): Record<string, NavCount> {
  const { reports: r, stats: s } = useSalesFigures()
  useEffect(() => {
    if (active) void loadSalesFigures()
    else resetSalesFigures()
  }, [active, activePage])
  // The shell going away (a mode switch, a logout) drops the figures too.
  useEffect(() => resetSalesFigures, [])
  return useMemo((): Record<string, NavCount> => {
    if (!active || !r || !s) return {}
    const excluded = r.openExcludedNonGbp ?? 0
    const accounts = s.accounts?.total
    const pending = s.activities?.pending
    return {
      'sales.pipeline': { value: r.openDealCount, excluded, title: excluded > 0
        ? `${r.openDealCount} open deals in GBP; ${excluded} excluded — uncaptured non-GBP`
        : `${r.openDealCount} open deals` },
      'sales.contacts': { value: s.contacts.total, title: `${s.contacts.total} contacts` },
      ...(isCount(accounts) ? { 'sales.accounts': { value: accounts, title: `${accounts} accounts` } } : {}),
      ...(isCount(pending) ? { 'sales.activities': { value: pending, title: `${pending} pending activities` } } : {}),
    }
  }, [active, r, s])
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface DashboardLayoutProps {
  activePage: string
  onNavigate: (page: string) => void
  children: React.ReactNode
  onSwitchMode: () => void
  /** Clears the stored token and returns to the login page. Omitted in stub mode. */
  onLogout?: () => void
  /**
   * Slice 2b, the PRIMARY-ACTION SLOT (ADOPTION.md §13.5 (4)): a page's one
   * primary action ("+ New Deal", "+ New Contact"), rendered in the bar to the
   * right of the breadcrumb. Absent → no slot element at all, so a page with
   * no primary action invents nothing. Nothing passes it yet: each page slice
   * moves its own button in when that page is re-shot.
   */
  primaryAction?: React.ReactNode
  /** Deal detail slice 1 (§39.9, spec §3.1): a third crumb — "Sales › Pipeline
   *  › {deal}". When present the page crumb becomes the way back (a button;
   *  there is still no router) and the tail is the current page. */
  crumbTail?: React.ReactNode
  onCrumbPage?: () => void
  /** The bar's right-hand page navigation ("Deal 2 of 9 · New Lead", ‹ ›),
   *  rendered between the breadcrumb and the primary-action slot. */
  pageNav?: React.ReactNode
  /**
   * Slice 3 (Sales Home): the page's OWN main padding — the boards' main
   * padding differs per page (Home `36px 40px 32px`, Pipeline / Contacts
   * `28px 40px 24px`), so each page slice passes its value from Dashboard.tsx
   * and every other page keeps the `22px 24px 44px` default (ADOPTION.md §13.1).
   */
  mainPadding?: string
  /** The backend-resolved tenant name (GET /v1/me). null until loaded — nothing renders. */
  tenantName?: string | null
  /** The session user's display name (GET /v1/me). Unset for WorkOS users with no name — email is the fallback. */
  userDisplayName?: string | null
  /** The session user's email (GET /v1/me). Absent for dev-token operators — no name line renders. */
  userEmail?: string | null
  /**
   * Feature-entitlement check for feature-tagged nav items (GET /v1/me
   * features). FAIL CLOSED: omitted, or /me not yet loaded, hides every
   * tagged item — the server-side 403 stays the real boundary.
   */
  hasFeature?: (key: string) => boolean
  /**
   * The scoped scroll-model variant for viewport-owning screens (S-Tier slice
   * 3 / ADOPTION.md §6.5: the pipeline board). The .dash-shell grid already
   * bounds the viewport; what this flips is the MAIN region: normally it is
   * the page scroller (overflow-y-auto + .k-page padding), but a
   * viewport-owning page brings its own nested scrollers (.k-board /
   * .k-lane), so main stops scrolling and stops padding — the page fills it
   * exactly. Dashboard.tsx sets this per RENDERED page (not per route id:
   * sales.pipeline renders the normally-scrolling Deal Detail while a deal is
   * drilled). Every other screen keeps the scrolling default.
   */
  viewportOwning?: boolean
}

// Pages the boards list under TOOLS rather than Workspace (slice 2b (2)).
// They stay in DEPARTMENTS so the active department still derives from them.
const TOOL_PAGES = new Set(['sales.archive'])

// S2 (RULED 2026-10-11 (1)): both toggles announce both chords; the visible
// title names the one this platform uses.
const SIDEBAR_KEYSHORTCUTS = 'Meta+Backslash Control+Backslash'

// ── The department switcher (S1, RULED 2026-10-10 Q1) ───────────────────────
// The header block IS the switcher: a menu button (the K tile, "Klaxiora", the
// active department — or, on a department-less page, that page's own name —
// and a chevron) whose stable accessible name is "Switch department", so the
// inventory keys one control whatever department is active. Its menu holds
// the landing FIRST (a plain menuitem, "Dashboard" — the breadcrumb's word for
// overview.home) and then every visible department as a menuitemradio,
// aria-checked on the active one; a pick lands on the department's FIRST page
// (the flat switcher's switchDepartment semantics, kept). Keyboard model:
// useShellMenu. A popover, never aria-modal.
// S2: the menu is positioned against the HEADER ROW (.k-sidebar-head), not
// this wrapper, so the expanded menu keeps the row's full width beside the
// collapse button; in the rail it opens to the right (klaxiora.css).
function DepartmentSwitcher({ departments, activeDept, currentLabel, onNavigate, closeSignal, rail }: {
  departments: Department[]
  activeDept: Department | null
  currentLabel: string
  onNavigate: (page: string) => void
  closeSignal: number
  /** S3a: in the rail the K tile carries the tooltip "Klaxiora · {department}". */
  rail: boolean
}) {
  const { open, toggle, triggerRef, menuRef, onTriggerKey, onMenuKey, pick } = useShellMenu<HTMLButtonElement, HTMLDivElement>(closeSignal)
  return (
    <div className="flex-1 min-w-0">
      <button
        ref={triggerRef}
        type="button"
        className="k-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'k-dept-menu' : undefined}
        aria-label="Switch department"
        data-tooltip={rail ? `Klaxiora · ${currentLabel}` : undefined}
        onClick={toggle}
        onKeyDown={onTriggerKey}
      >
        {/* The boards' 28px rounded-8 "K" square: the primary ink with the
            panel ink for the letter, so it reads on both grounds without a
            brand token. */}
        <span className="w-7 h-7 rounded-[8px] flex items-center justify-center text-14 font-bold flex-shrink-0"
          style={{ background: 'var(--text)', color: 'var(--panel)' }} aria-hidden="true">K</span>
        <span className="k-rail-label flex-1 min-w-0 flex flex-col leading-tight">
          <span className="text-14 font-semibold text-ink-primary truncate">Klaxiora</span>
          <span className="text-12 text-ink-secondary flex items-center gap-[2px] min-w-0">
            <span className="truncate">{currentLabel}</span>
            <ChevronDown size={12} className="flex-shrink-0" aria-hidden="true" />
          </span>
        </span>
      </button>
      {open && (
        <div ref={menuRef} id="k-dept-menu" role="menu" aria-label="Department menu" className="k-menu k-menu--below" onKeyDown={onMenuKey}>
          <button type="button" role="menuitem" className="k-menuitem" onClick={pick(() => onNavigate('overview.home'))}>
            <LayoutGrid size={16} strokeWidth={1.9} className="flex-shrink-0" /> {PAGE_TITLES['overview.home']}
          </button>
          {departments.map(dept => {
            const checked = activeDept?.id === dept.id
            return (
              <button
                key={dept.id}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                className="k-menuitem"
                onClick={pick(() => onNavigate(dept.items[0].id))}
              >
                <span className="k-truncate">{dept.label}</span>
                {checked && <span className="k-dot ml-auto" aria-hidden="true" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── The account menu (slice 2b, RULED 2026-09-27 (3); S1 added Theme) ───────
// The user footer is a MENU BUTTON. Its visible content is the avatar, the
// session user and the active tenant; its accessible name is the stable
// "Account menu" so the inventory keys one control across sessions (the
// user's name would rename it per login). The menu holds Settings, the THEME
// group (Light / Dark / Auto as menuitemradio, checked on the stored
// preference — S1, Q3) and Log out, which renders only when a handler exists,
// exactly as the bar's button did. Keyboard model: useShellMenu.
const THEME_CHOICES: { value: ThemePreference; label: string; Icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'auto', label: 'Auto', Icon: Monitor },
]
function AccountMenu({ userInitials, userLabel, tenantName, onNavigate, onLogout, closeSignal, rail }: {
  userInitials: string | null
  userLabel: string | null
  tenantName?: string | null
  onNavigate: (page: string) => void
  onLogout?: () => void
  closeSignal: number
  /** S3a: in the rail the title rides data-tooltip instead. */
  rail: boolean
}) {
  const { open, toggle, triggerRef, menuRef, onTriggerKey, onMenuKey, pick } = useShellMenu<HTMLButtonElement, HTMLDivElement>(closeSignal)
  const theme = useTheme()
  const title = [userLabel, tenantName].filter(Boolean).join(' · ') || 'Account'
  return (
    <div className="k-menu-anchor mt-auto pt-3 relative flex-shrink-0">
      {open && (
        <div ref={menuRef} id="k-account-menu" role="menu" aria-label="Account menu" className="k-menu" onKeyDown={onMenuKey}>
          <button type="button" role="menuitem" className="k-menuitem" onClick={pick(() => onNavigate('settings.company'))}>
            <Settings size={16} className="flex-shrink-0" /> Settings
          </button>
          <div role="group" aria-labelledby="k-theme-caption" className="k-menugroup">
            <div id="k-theme-caption" className="k-menucaption">Theme</div>
            {THEME_CHOICES.map(({ value, label, Icon }) => (
              <button key={value} type="button" role="menuitemradio" aria-checked={theme === value} className="k-menuitem" onClick={pick(() => setTheme(value))}>
                <Icon size={16} className="flex-shrink-0" /> {label}
              </button>
            ))}
          </div>
          {onLogout && (
            <button type="button" role="menuitem" className="k-menuitem k-menuitem--danger" onClick={pick(onLogout)}>
              <LogOut size={16} className="flex-shrink-0" /> Log out
            </button>
          )}
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        className="k-user"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'k-account-menu' : undefined}
        aria-label="Account menu"
        title={rail ? undefined : title}
        data-tooltip={rail ? title : undefined}
        onClick={toggle}
        onKeyDown={onTriggerKey}
      >
        <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: track.DEFAULT }} aria-hidden="true">
          {userInitials
            ? <span className="text-12 font-semibold leading-none text-ink-primary">{userInitials}</span>
            : <UserRound size={15} className="text-ink-secondary" />}
        </span>
        <span className="k-rail-label flex-1 min-w-0">
          {userLabel && (
            <span className="block text-13 font-semibold text-ink-primary leading-tight truncate">{userLabel}</span>
          )}
          {/* Active tenant — the session scope the backend actually resolved (/v1/me). */}
          {tenantName && (
            <span className="block text-11 text-ink-secondary truncate" title="Active tenant">{tenantName}</span>
          )}
        </span>
        <ChevronsUpDown size={14} className="k-rail-hide flex-shrink-0 text-ink-secondary" aria-hidden="true" />
      </button>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DashboardLayout({ activePage, onNavigate, children, onSwitchMode, onLogout, primaryAction, crumbTail, onCrumbPage, pageNav, mainPadding, tenantName, userDisplayName, userEmail, hasFeature, viewportOwning = false }: DashboardLayoutProps) {
  // Feature-tagged nav items render only for entitled tenants (fail closed:
  // no hasFeature or no /me yet → tagged items hidden). Untagged items are
  // unaffected; a department whose items all hide collapses away entirely.
  const visibleDepartments = DEPARTMENTS
    .filter(dept => !NAV_HIDDEN[dept.id])
    .map(dept => ({ ...dept, items: dept.items.filter(item => !item.feature || (hasFeature?.(item.feature) ?? false)) }))
    .filter(dept => dept.items.length > 0)
  // Derived, never stored (§14.C): the active department is a pure function
  // of the route, so the switcher's checked row and the Workspace list cannot
  // desync.
  const activeDept = activeDepartment(visibleDepartments, activePage)

  const navCounts = useSalesNavCounts(activeDept?.id === 'sales', activePage)
  const userLabel = userDisplayName || userEmail || null
  const userInitials = userDisplayName
    ? userDisplayName.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : userEmail
      ? userEmail.split('@')[0].slice(0, 2).toUpperCase()
      : null

  // The remembered Tools state (S1): the stored value under the /v1/me
  // identity's key (open until one exists; nothing is written without one),
  // overridden by this session's clicks. The boot read resolves after the
  // first render, so the stored value is re-read when the key appears, and a
  // click made under one key does not carry over to another.
  const toolsKey = sidebarToolsPrefKey(cachedMeIdentity())
  const toolsStored = useMemo(() => readToolsOpen(toolsKey), [toolsKey])
  const [toolsClick, setToolsClick] = useState<{ key: string | null; open: boolean } | null>(null)
  const toolsOpen = toolsClick && toolsClick.key === toolsKey ? toolsClick.open : toolsStored
  const toggleTools = () => {
    const next = !toolsOpen
    writeToolsOpen(toolsKey, next)
    setToolsClick({ key: toolsKey, open: next })
  }

  // S2: the rail. Read once, synchronously, at the first render (a device
  // key needs no identity). `animate` turns the CSS transition on only after
  // the first toggle; `menuClose` closes an open shell menu on every toggle;
  // `focusCounterpart` carries "the focused toggle is about to disappear"
  // across the render that swaps it.
  //
  // S3b: below 1280 the rail is FORCED — effective = forcedRail || stored.
  // A width change never writes the stored value and never animates; while
  // forced, a person's toggle expands IN PLACE for this session only (writes
  // nothing) and a second press returns to the rail; leaving the forced range
  // drops that override, and re-entering it starts from the rail again. The
  // forced value seen last render is kept so a change is handled in the render
  // that observes it (React's "store information from previous renders").
  const forcedRail = useForcedRail()
  const [stored, setStored] = useState(readSidebarCollapsed)
  const [storedTouched, setStoredTouched] = useState(false)
  const [expandedInPlace, setExpandedInPlace] = useState(false)
  const [animate, setAnimate] = useState(false)
  const [seenForced, setSeenForced] = useState(forcedRail)
  if (seenForced !== forcedRail) {
    setSeenForced(forcedRail)
    setExpandedInPlace(false)
    setAnimate(false)
  }
  // S3c: below 768 the drawer replaces the rail entirely (collapsed is false
  // there: the drawer shows the expanded nav). Its open state is never stored
  // and is dropped — with the animations — when the mode changes.
  const drawerMode = useSidebarDrawerMode()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerAnimate, setDrawerAnimate] = useState(false)
  const [seenDrawerMode, setSeenDrawerMode] = useState(drawerMode)
  if (seenDrawerMode !== drawerMode) {
    setSeenDrawerMode(drawerMode)
    setDrawerOpen(false)
    setDrawerAnimate(false)
    setAnimate(false)
  }
  const collapsed = drawerMode ? false : (forcedRail ? !expandedInPlace : stored)
  const [menuClose, setMenuClose] = useState(0)
  const collapseRef = useRef<HTMLButtonElement>(null)
  const expandRef = useRef<HTMLButtonElement>(null)
  const focusCounterpart = useRef(false)
  // Focus inside an open shell menu goes back to that menu's trigger (the K
  // tile, the avatar — both survive the toggle), found BEFORE the render that
  // unmounts the menu, through the trigger's aria-controls.
  const refocusTrigger = useRef<HTMLElement | null>(null)
  // S3a: the rail's one tooltip, delegated from the aside; live only collapsed.
  const asideRef = useRef<HTMLElement>(null)
  const { portal: railTooltip, hide: hideRailTooltip } = useRailTooltip(asideRef, collapsed)
  // S3c: the drawer. It never unmounts, so the trap's return-on-unmount is off
  // and the drawer returns focus itself: `drawerFocus` carries where focus goes
  // across the render that opens or closes it.
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  const drawerCloseRef = useRef<HTMLButtonElement>(null)
  const drawerFocus = useRef<'panel' | 'menu' | null>(null)
  useFocusTrap(asideRef, drawerMode && drawerOpen, { returnFocusOnUnmount: false })
  const openDrawer = useCallback(() => {
    drawerFocus.current = 'panel'
    setDrawerAnimate(true)
    setDrawerOpen(true)
  }, [])
  const closeDrawer = useCallback((returnFocus: boolean) => {
    drawerFocus.current = returnFocus ? 'menu' : null
    setMenuClose(n => n + 1)
    setDrawerOpen(false)
  }, [])
  useEffect(() => {
    const where = drawerFocus.current
    drawerFocus.current = null
    if (where === 'panel') drawerCloseRef.current?.focus()
    else if (where === 'menu') menuBtnRef.current?.focus()
  }, [drawerOpen])
  // Esc closes the drawer — unless a shell menu inside it is open (Esc is the
  // menu's) or another modal is (it owns the keyboard).
  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (asideRef.current?.querySelector('[role="menu"]') || isOtherModalOpen()) return
      closeDrawer(true)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen, closeDrawer])
  // Choosing a destination in the drawer closes it, with no focus return.
  const navigate = useCallback((id: string) => {
    if (drawerOpen) closeDrawer(false)
    onNavigate(id)
  }, [drawerOpen, closeDrawer, onNavigate])
  const switchMode = useCallback(() => {
    if (drawerOpen) closeDrawer(false)
    onSwitchMode()
  }, [drawerOpen, closeDrawer, onSwitchMode])
  const toggleSidebar = useCallback(() => {
    if (drawerMode) {
      if (drawerOpen) closeDrawer(true)
      else openDrawer()
      return
    }
    hideRailTooltip()
    const active = document.activeElement
    focusCounterpart.current = active !== null && (active === collapseRef.current || active === expandRef.current)
    const menu = active?.closest<HTMLElement>('#k-sidebar [role="menu"]')
    refocusTrigger.current = menu?.id ? document.querySelector<HTMLElement>(`[aria-controls="${menu.id}"]`) : null
    setMenuClose(n => n + 1)
    setAnimate(true)
    if (forcedRail) {
      setExpandedInPlace(e => !e)
    } else {
      setStoredTouched(true)
      setStored(c => !c)
    }
  }, [hideRailTooltip, forcedRail, drawerMode, drawerOpen, openDrawer, closeDrawer])
  // Written after a person's toggle while NOT forced only — the first render
  // reads, it never writes, and neither a width change nor a forced toggle does.
  useEffect(() => { if (storedTouched) writeSidebarCollapsed(stored) }, [storedTouched, stored])
  useEffect(() => {
    const trigger = refocusTrigger.current
    refocusTrigger.current = null
    if (trigger) trigger.focus()
    if (!focusCounterpart.current) return
    focusCounterpart.current = false
    ;(collapsed ? expandRef : collapseRef).current?.focus()
  }, [collapsed])
  // The chord: ONE document listener for the shell's life. The key is named
  // positively (`.key ===`) so the affordance inventory can read it.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === '\\' && sidebarChordEligible(e)) {
        e.preventDefault()
        toggleSidebar()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [toggleSidebar])
  const chordHint = /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? '⌘\\' : 'Ctrl+\\'

  // The breadcrumb: the department's sidebar label › the row's sidebar label;
  // a department-less page (the landing Dashboard, Settings) is one crumb,
  // from PAGE_TITLES. Derived every render — it cannot desync from the rows.
  const pageLabel = activeDept?.items.find(i => i.id === activePage)?.label
    ?? PAGE_TITLES[activePage]
    ?? (() => {
      const parts = activePage.split('.')
      return parts[parts.length - 1].replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
    })()

  return (
    <div className="dash-shell" data-sidebar={drawerMode ? 'drawer' : collapsed ? 'collapsed' : 'expanded'}
      data-sidebar-animate={animate ? '' : undefined} data-drawer-animate={drawerAnimate ? '' : undefined}>
      {/* ── Sidebar — the S1 geometry: 224px (layout['sidebar-w'] via the
             .dash-shell grid), 12px between blocks, 1px between rows. ── */}
      <aside
        ref={asideRef}
        id="k-sidebar"
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? true : undefined}
        aria-label={drawerOpen ? 'Navigation drawer' : undefined}
        data-drawer-open={drawerOpen ? '' : undefined}
        className="row-start-1 row-end-3 col-start-1 col-end-2 flex flex-col overflow-hidden gap-3 pt-[14px] px-[14px] pb-[14px]"
        style={{
          background: surface.sidebar,
          borderRight: `1px solid ${edge.sidebar}`,
          gridArea: 'sidebar',
        }}
      >
        {/* Header row (S1 the switcher; S2 the collapse button at its right,
            expanded only). The row is the switcher menu's positioning box. */}
        <div className="k-sidebar-head k-menu-anchor relative flex-shrink-0">
          <DepartmentSwitcher
            departments={visibleDepartments}
            activeDept={activeDept}
            currentLabel={activeDept?.label ?? pageLabel}
            onNavigate={navigate}
            closeSignal={menuClose}
            rail={collapsed}
          />
          {drawerMode ? (
            <button
              ref={drawerCloseRef}
              type="button"
              className="k-sidebar-toggle"
              aria-label="Close navigation"
              onClick={() => closeDrawer(true)}
            >
              <X size={17} strokeWidth={1.9} aria-hidden="true" />
            </button>
          ) : !collapsed && (
            <button
              ref={collapseRef}
              type="button"
              className="k-sidebar-toggle"
              aria-label="Collapse sidebar"
              aria-expanded={true}
              aria-controls="k-sidebar"
              aria-keyshortcuts={SIDEBAR_KEYSHORTCUTS}
              title={`Collapse sidebar (${chordHint})`}
              onClick={toggleSidebar}
            >
              <PanelLeft size={17} strokeWidth={1.9} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto flex flex-col gap-3" style={{ scrollbarWidth: 'none' }}>
          {/* WORKSPACE — only the active department's pages, icon+label, Home
              first (its first page). No caption: the spec's main nav has no
              section label. Hidden on dept-less routes (overview.home /
              settings) — nothing honest to show. */}
          {activeDept && (
            <div role="group" aria-label="Workspace" className="flex flex-col gap-px">
              {activeDept.items.filter(item => !TOOL_PAGES.has(item.id)).map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  Icon={NAV_ICONS[item.id] ?? LayoutGrid}
                  current={activePage === item.id}
                  count={navCounts[item.id]}
                  onNavigate={navigate}
                  rail={collapsed}
                />
              ))}
            </div>
          )}

          {/* S2: the rail's hairline between Workspace and Tools — only where
              a Workspace group renders (the landing has none, so no divider). */}
          {collapsed && activeDept && <div role="separator" className="k-rail-divider" />}

          {/* TOOLS (slice 2b (2); S1 made the caption a remembered BUTTON):
              Mission Control (the mode switch — same name, same handler as
              the bar's button once had) and Archive, the department's own
              page rendered under Tools instead of Workspace, so it appears
              only while its department is active. Collapsed → the group is
              not rendered; the caption's aria-expanded says so. */}
          <div className="flex flex-col gap-px">
            {/* S2: collapsed, the caption is not rendered and the Tools are
                always shown; the stored caption state is left as it was. */}
            {!collapsed && (
              <button
                type="button"
                className="k-navcaption"
                aria-expanded={toolsOpen}
                aria-controls={toolsOpen ? 'k-tools' : undefined}
                onClick={toggleTools}
              >
                Tools
                <ChevronDown size={12} className={`flex-shrink-0 transition-transform ${toolsOpen ? '' : '-rotate-90'}`} aria-hidden="true" />
              </button>
            )}
            {(collapsed || toolsOpen) && (
              <div id="k-tools" role="group" aria-label="Tools" className="flex flex-col gap-px">
                <NavRow
                  id="tool.mission-control"
                  label="Mission Control"
                  Icon={Zap}
                  current={false}
                  kind="tool"
                  title="Open Mission Control"
                  onActivate={switchMode}
                  onNavigate={navigate}
                  rail={collapsed}
                />
                {activeDept?.items.filter(item => TOOL_PAGES.has(item.id)).map(item => (
                  <NavRow
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    Icon={NAV_ICONS[item.id] ?? LayoutGrid}
                    current={activePage === item.id}
                    count={navCounts[item.id]}
                    onNavigate={navigate}
                    rail={collapsed}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Platform section — flag-hidden (§14.D), not deleted. */}
          {SHOW_PLATFORM_SECTION && (
            <div className="flex flex-col gap-px">
              <div className="k-navgroup-label">Platform</div>
              {[
                { id: 'overview.health',      label: 'System Health',
                  trailing: <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent['health-dot'] }} /> },
                // M7b: operations.agents now renders the field-team page, so
                // the AI-agent AgentsList lives on at its dormant
                // automation.agents alias — this row is its only sidebar path.
                { id: 'automation.agents',    label: 'AI Agents' },
                { id: 'analytics.performance',label: 'Run Analytics' },
                { id: 'analytics.models',     label: 'Model Usage' },
              ].map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  current={activePage === item.id}
                  trailing={item.trailing}
                  onNavigate={navigate}
                />
              ))}
            </div>
          )}
        </nav>

        {/* Pinned user footer — the session user (/v1/me): display name, else
            email; a generic avatar for dev-token operators. Slice 2b: the
            footer IS the account menu's button (Settings, Theme, Log out). */}
        {/* S2: the expand button, at the bottom of the rail above the avatar. */}
        {collapsed && (
          <button
            ref={expandRef}
            type="button"
            className="k-sidebar-toggle k-sidebar-expand flex-shrink-0"
            aria-label="Expand sidebar"
            aria-expanded={false}
            aria-controls="k-sidebar"
            aria-keyshortcuts={SIDEBAR_KEYSHORTCUTS}
            data-tooltip={`Expand sidebar (${chordHint})`}
            onClick={toggleSidebar}
          >
            <PanelLeftOpen size={18} strokeWidth={1.9} aria-hidden="true" />
          </button>
        )}
        <AccountMenu userInitials={userInitials} userLabel={userLabel} tenantName={tenantName} onNavigate={navigate} onLogout={onLogout} closeSignal={menuClose} rail={collapsed} />
      </aside>
      {/* S3c: the drawer's scrim — a click closes it (focus back to the menu
          button). Decorative to assistive tech: Esc and the close button are
          the named ways out. */}
      {drawerOpen && <div className="k-drawer-scrim" data-sidebar-scrim="" aria-hidden="true" onClick={() => closeDrawer(true)} />}

      {/* ── Topbar — .k-topbar treatment (0 40px, the translucent toolbar ground,
             its own hairline); its height is layout['topbar-h'] (64px), the
             same value the .dash-shell grid row reads. The bar carries the
             BREADCRUMB, not the page title (slice 2), and the primary-action
             slot — nothing else since S1 (the theme capsule moved into the
             account menu). ── */}
      <header
        inert={drawerOpen || undefined}
        className="flex items-center gap-3 px-10"
        style={{
          gridArea: 'header',
          background: surface.header,
          borderBottom: `1px solid ${edge.topbar}`,
          height: layout['topbar-h'],
        }}
      >
        {/* The breadcrumb is a LIST (slice 2b): each crumb its own item, so a
            screen reader separates "Sales" from "Dashboard"; the chevron is
            decorative. Text, not links — no router (row 54). */}
        {/* S3c: the drawer's opener, drawer mode only (below 768). */}
        {drawerMode && (
          <button
            ref={menuBtnRef}
            type="button"
            className="k-sidebar-toggle k-drawer-opener flex-shrink-0"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            aria-controls="k-sidebar"
            onClick={openDrawer}
          >
            <Menu size={18} strokeWidth={1.9} aria-hidden="true" />
          </button>
        )}
        <nav aria-label="Breadcrumb" className="flex-1 min-w-0">
          <ol className="k-breadcrumb">
            {activeDept && (
              <li><span className="k-breadcrumb-dept">{activeDept.label}</span></li>
            )}
            <li>
              {activeDept && <ChevronRight size={12} className="flex-shrink-0 text-ink-disabled" aria-hidden="true" />}
              {crumbTail !== undefined ? (
                // The page crumb is the way back to its list (spec §3.1: "the
                // breadcrumb does that job"); a button, since there is no router.
                <button type="button" className="k-breadcrumb-page hover:underline bg-transparent border-0 p-0 cursor-pointer"
                  style={{ font: 'inherit', fontWeight: 600 }} onClick={onCrumbPage}>{pageLabel}</button>
              ) : (
                <span className="k-breadcrumb-page" aria-current="page">{pageLabel}</span>
              )}
            </li>
            {crumbTail !== undefined && (
              <li>
                <ChevronRight size={12} className="flex-shrink-0 text-ink-disabled" aria-hidden="true" />
                <span className="k-breadcrumb-page k-truncate" aria-current="page">{crumbTail}</span>
              </li>
            )}
          </ol>
        </nav>

        {pageNav && (
          <div className="flex-shrink-0 flex items-center gap-2" data-slot="page-nav">{pageNav}</div>
        )}

        {/* The primary-action SLOT (slice 2b (4)): rendered only when a page
            passes one. No page does yet. */}
        {primaryAction && (
          <div className="flex-shrink-0 flex items-center" data-slot="primary-action">{primaryAction}</div>
        )}

        {/* No search capsule and no ⌘K here since slice 2b (RULED 2026-09-27
            (d)): the control was dead on every page; honest search is row 63.
            No theme capsule since S1 (Q3): Theme is the account menu's group.
            No bell. The shell once rendered a bell button with no handler, no
            accessible name and a hard-coded red dot — an "unread" bound to no
            read path. It returns WITH a notification channel, never before
            one (data honesty outranks the Stier README's "notification dot"). */}
      </header>

      {/* ── Main — .k-page padding (22px 24px 44px). The flex/gap half of
             .k-page is deliberately NOT applied: pages own their own internal
             spacing today, and adding a parent gap would double-space them.
             A viewportOwning page (the pipeline board) instead gets an
             unpadded, non-scrolling main it fills exactly — its .k-pipeline
             head/board carry their own padding and scrolling (§6.5). ── */}
      <main
        inert={drawerOpen || undefined}
        className={viewportOwning ? 'overflow-hidden' : 'overflow-y-auto'}
        style={{
          gridArea: 'main',
          background: surface.content,
          padding: viewportOwning ? 0 : (mainPadding ?? '22px 24px 44px'),
        }}
      >
        {children}
      </main>
      {railTooltip}
    </div>
  )
}
